import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { ConfirmDialog } from '@/design-system';
import { useAuth } from '@/core/auth';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { AnnouncementsTable } from './components/AnnouncementsTable';
import { AnnouncementEditorDialog } from './components/AnnouncementEditorDialog';
import { useAnnouncementsListQuery } from './hooks/useAnnouncementsListQuery';
import { useAnnouncementMutations } from './hooks/useAnnouncementMutations';
import { announcementsService } from './api/announcements.service';
import type { Announcement, AnnouncementStatus } from './types';

const PAGE_LIMIT = 20;

export function AnnouncementsPage() {
  const { user } = useAuth();
  const canAccess = user?.role === 'SUPERADMIN' || user?.role === 'SUPPORT';

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AnnouncementStatus | undefined>(undefined);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState<Announcement | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const listFilters = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      search: debouncedSearch || undefined,
      status: statusFilter,
    }),
    [page, debouncedSearch, statusFilter]
  );

  const { data, isLoading, isError, error } = useAnnouncementsListQuery(listFilters);
  const mutations = useAnnouncementMutations();
  const onErrorToast = useErrorToast();

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar comunicados');
  }, [isError, error, onErrorToast]);

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const announcements = data?.announcements ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const closeEditor = () => {
    setShowCreate(false);
    setShowEdit(false);
    setSelected(null);
  };

  const loadDetail = async (a: Announcement) => {
    setLoadingDetail(true);
    try {
      const detail = await announcementsService.get(a.id);
      setSelected(detail);
      return detail;
    } catch (e) {
      onErrorToast(e, 'Erro ao carregar comunicado');
      return null;
    } finally {
      setLoadingDetail(false);
    }
  };

  const publishEstimate =
    selected?.audience === 'all'
      ? 'todas as empresas ativas'
      : `${selected?.companyIds?.length ?? 0} empresa(s) selecionada(s)`;

  return (
    <DashboardLayout title="Comunicados">
      <AnnouncementsTable
        announcements={announcements}
        isLoading={isLoading || loadingDetail}
        search={search}
        onSearchChange={setSearch}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={PAGE_LIMIT}
        onPageChange={setPage}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onNew={() => {
          setSelected(null);
          setShowCreate(true);
        }}
        onEdit={(a) => {
          void loadDetail(a).then((detail) => {
            if (detail) setShowEdit(true);
          });
        }}
        onPublish={(a) => {
          void loadDetail(a).then((detail) => {
            if (detail) setShowPublish(true);
          });
        }}
        onArchive={(a) => {
          setSelected(a);
          setShowArchive(true);
        }}
        onDelete={(a) => {
          setSelected(a);
          setShowDelete(true);
        }}
      />

      <AnnouncementEditorDialog
        isOpen={showCreate || showEdit}
        mode={showCreate ? 'create' : 'edit'}
        announcement={showEdit ? selected : null}
        onClose={closeEditor}
        mutations={{
          createAnnouncement: mutations.createAnnouncement,
          updateAnnouncement: mutations.updateAnnouncement,
          uploadMedia: mutations.uploadMedia,
        }}
      />

      <ConfirmDialog
        isOpen={showPublish}
        onClose={() => {
          setShowPublish(false);
          setSelected(null);
        }}
        onConfirm={() => {
          if (!selected) return;
          void mutations.publishAnnouncement.mutateAsync(selected.id).then(() => {
            setShowPublish(false);
            setSelected(null);
          });
        }}
        title="Publicar comunicado"
        message={`Publicar "${selected?.title ?? ''}" para ${publishEstimate}? Os clientes passarão a ver o aviso no painel.`}
        confirmText="Publicar"
        cancelText="Cancelar"
        isLoading={mutations.publishAnnouncement.isPending}
      />

      <ConfirmDialog
        isOpen={showArchive}
        onClose={() => {
          setShowArchive(false);
          setSelected(null);
        }}
        onConfirm={() => {
          if (!selected) return;
          void mutations.archiveAnnouncement.mutateAsync(selected.id).then(() => {
            setShowArchive(false);
            setSelected(null);
          });
        }}
        title="Arquivar comunicado"
        message={`Arquivar "${selected?.title ?? ''}"? Ele deixa de aparecer para os clientes.`}
        confirmText="Arquivar"
        cancelText="Cancelar"
        isLoading={mutations.archiveAnnouncement.isPending}
      />

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => {
          setShowDelete(false);
          setSelected(null);
        }}
        onConfirm={() => {
          if (!selected) return;
          void mutations.deleteAnnouncement.mutateAsync(selected.id).then(() => {
            setShowDelete(false);
            setSelected(null);
          });
        }}
        title="Excluir comunicado"
        message={`Excluir o rascunho "${selected?.title ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
        isLoading={mutations.deleteAnnouncement.isPending}
      />
    </DashboardLayout>
  );
}
